// Leitura do rastro de execução de um PowerAgent.
//
// O rastro é gravado pelo runtime e chega como `response_preview`: um JSON já ENCOLHIDO —
// valores longos viram marcadores de tamanho, listas grandes viram cabeça + "«+N itens»".
// Encolhido, não truncado: continua sendo JSON válido, e é por isso que dá para interpretar
// aqui em vez de mostrar texto cru. Quando não dá, a resposta é `null` e quem chama mostra o
// texto — inventar um estado a partir de metade de um objeto seria pior que não mostrar.
//
// Vive aqui, e não dentro de um componente, porque são dois: o painel de depuração da
// conversa e o console do Playground. Duas leituras do mesmo formato divergem no dia em que
// o formato mudar, e a que divergir vai ser a que ninguém está olhando.

export function parseToolResponse(preview) {
  if (!preview) return null;
  try {
    return JSON.parse(preview);
  } catch (e) {
    return null;
  }
}

// Quebra de linha e recuo de dois espaços, como o `JSON.stringify(v, null, 2)` do `args`, mas
// sobre o TEXTO. Passar a prévia por `JSON.parse` arredonda inteiro acima de 2^53: um id de pedido
// de 19 dígitos apareceria errado na tela de depuração, que é justamente onde se confere o id que
// a API devolveu. Só chega aqui texto que o `JSON.parse` aceitou, então basta saber quando se
// está dentro de uma string.
function recuarJson(texto) {
  let saida = '';
  let nivel = 0;
  let emString = false;
  let escapado = false;
  const quebra = () => `\n${'  '.repeat(nivel)}`;

  for (let i = 0; i < texto.length; i += 1) {
    const c = texto[i];
    if (emString) {
      saida += c;
      if (escapado) escapado = false;
      else if (c === '\\') escapado = true;
      else if (c === '"') emString = false;
    } else if (c === '"') {
      emString = true;
      saida += c;
    } else if (c === '{' || c === '[') {
      const fecho = c === '{' ? '}' : ']';
      let j = i + 1;
      while (/\s/.test(texto[j] || '')) j += 1;
      if (texto[j] === fecho) {
        saida += c + fecho;
        i = j;
      } else {
        nivel += 1;
        saida += c + quebra();
      }
    } else if (c === '}' || c === ']') {
      nivel -= 1;
      saida += quebra() + c;
    } else if (c === ',') {
      saida += `,${quebra()}`;
    } else if (c === ':') {
      saida += ': ';
    } else if (!/\s/.test(c)) {
      saida += c;
    }
  }
  return saida;
}

// A prévia INTEIRA e indentada, para o Playground e o painel de depuração lerem igual. O que não
// é JSON (texto de erro, página HTML) volta como veio: esconder atrás de um erro seria pior.
export function previaIndentada(previa) {
  if (!previa) return null;
  try {
    JSON.parse(previa);
  } catch (e) {
    return previa;
  }
  return recuarJson(previa);
}

// A busca em base de conhecimento: em quantas bases procurou, quantos trechos trouxe, qual o
// melhor escore ANTES do corte, e DE ONDE veio cada trecho.
//
// `topo` é o campo que separa os três estados que pareciam um só — sem base conectada,
// buscou e não achou, achou — e vem antes de qualquer filtro de propósito: é ele que
// distingue "o melhor foi 0,26" de "não havia nada indexado".
// Os três estados que o status HTTP não distingue — 200 é 200 nos três: não havia base
// conectada, buscou nas bases e não achou, achou.
function estadoDaBusca(bases, achou) {
  if (!bases.length) return 'sem_base';
  return achou > 0 ? 'achou' : 'vazio';
}

// Quem não é super admin recebe o rastro projetado no servidor (`TraceProjection`), sem o
// `response_preview`: a leitura da busca vem pronta em `resumo.fontes`, com os mesmos números.
function leituraDaProjecao(fontes) {
  if (!fontes) return null;
  const bases = Array.isArray(fontes.bases) ? fontes.bases : [];
  const achou = Number(fontes.achou || 0);
  return {
    bases,
    achou,
    topo: fontes.top_score,
    fontes: Array.isArray(fontes.itens) ? fontes.itens : [],
    fontesOcultas: Number(fontes.ocultas || 0),
    estado: estadoDaBusca(bases, achou),
  };
}

export function leituraDaBusca(call) {
  if (!call || call.tool_name !== 'query_knowledge') return null;
  const corpo = parseToolResponse(call.response_preview);
  if (!corpo) return leituraDaProjecao(call.resumo?.fontes);

  const bases = Array.isArray(corpo.stores_searched)
    ? corpo.stores_searched
    : [];
  const achou = Number(corpo.count || 0);
  // Itens que o encolhimento transformou em marcador ("«+2 itens»") são string, não objeto:
  // filtrar por tipo é o que evita renderizar `[object Object]` no lugar de uma fonte.
  const fontes = (Array.isArray(corpo.results) ? corpo.results : []).filter(
    f => f && typeof f === 'object'
  );

  return {
    bases,
    achou,
    topo: corpo.top_score,
    fontes,
    // Quantas fontes o rastro não guardou. Dizer isto é o que impede alguém de concluir
    // "a busca só trouxe 3" quando na verdade o rastro só coube 3.
    fontesOcultas: Math.max(0, achou - fontes.length),
    estado: estadoDaBusca(bases, achou),
  };
}

// Os três estados do cache do modelo, com os mesmos nomes que o runtime usa no `/usage`.
// Ler "cache_read: 0" exige saber de cor que o provedor tem piso de tamanho; o nome não.
export function estadoDoCache(turn) {
  if (Number(turn?.cache_read || 0) > 0) return 'ativo';
  if (Number(turn?.cache_write || 0) > 0) return 'escrita_sem_leitura';
  return 'sem_cache';
}

export function formatarCusto(valor) {
  if (valor === null || valor === undefined) return null;
  const n = Number(valor);
  if (!Number.isFinite(n)) return null;
  // Um turno custa milésimos de centavo. Com 2 casas todo turno vira "US$ 0,00", que é o
  // mesmo que não mostrar; com 6 dá para comparar dois turnos a olho.
  return `US$ ${n.toFixed(6)}`;
}
